import { useFormContext } from 'react-hook-form';

import { CasIconButton } from 'src/compass/shared/components';
import CasEditableDialog from 'src/compass/shared/components/CasTable/CasEditableDialog';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';

import ContactDetailsForm from './ContactDetailsForm';
import { preferredOnFile } from './contactPhones';

interface Props {
  // The Edit Customer Contacts row this pencil sits on (CasMultiRowTable's rowNameRef).
  rowNameRef: string;
  // The caller's onChanged - a HubSpot save is already done whether or not the editor is saved.
  onContactChanged?: VoidFunction;
}

/*
  The pencil in an Edit Customer Contacts row: opens Contact Details on that contact, which saves
  to HubSpot straight away.

  The editor stays open underneath with whatever Include and Preferred changes are still unsaved,
  so the saved addresses go into its row with setValue rather than by reloading it. A Preferred
  the save left without an address takes the server's answer (see HubspotContactDetailsBo) - for
  a removed additional email that is Email, the contact's primary, as the nightly import would
  leave it.
*/
export default function ContactDetailsButton({ rowNameRef, onContactChanged }: Props) {
  const dialog = useCasBoolean();
  const { getValues, setValue } = useFormContext();

  const applySaved = (saved: any) => {
    ['name', 'email', 'mobilephone', 'phone', 'business_phone'].forEach((field) =>
      setValue(`${rowNameRef}.${field}`, saved?.[field] ?? '')
    );
    setValue(`${rowNameRef}.additionalEmails`, saved?.additionalEmails ?? []);
    // Judged on the saved addresses, so the row's Invalid chips follow the save (story 15723).
    setValue(`${rowNameRef}.invalidAddresses`, saved?.invalidAddresses ?? []);

    if (!preferredOnFile(getValues(rowNameRef)))
      setValue(`${rowNameRef}.preferredChannel`, saved?.preferredChannel ?? '');

    onContactChanged?.();
  };

  return (
    <>
      <CasIconButton icon="eva:edit-fill" tooltip="Edit name, phones and emails" onClick={dialog.setTrue} />
      {/* Mounted only while open, as CustomerContactsButton does, so each open fetches afresh. */}
      {dialog.value && (
        <CasEditableDialog
          maxWidth="sm"
          entityTitle="Contact Details"
          entity="Hubspot/Contact"
          editForm={ContactDetailsForm}
          dialog={dialog}
          id={getValues(`${rowNameRef}.id`)}
          // Outside prod a contact not flagged as a HubSpot test contact opens read-only.
          canSave={(contact: any) => !!contact?.canEdit}
          onChanged={(_payload: any, response: any) => applySaved(response)}
        />
      )}
    </>
  );
}
